import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BIZ } from "@/lib/content";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-stone-900 text-stone-300 mt-20" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-teal-600 grid place-items-center text-white font-display font-bold">F</div>
              <div>
                <div className="font-display text-white text-lg font-semibold">FCA Reports</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-teal-400">Australia</div>
              </div>
            </div>
            <p className="text-sm text-stone-400 leading-relaxed">Independent, NDIS-aligned Functional Capacity Assessments delivered 100% virtually by experienced AHPRA-registered Occupational Therapists Australia-wide.</p>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Services</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/services/functional-capacity-assessment" className="hover:text-teal-400">Functional Capacity Assessment</Link></li>
              <li><Link to="/services/sil-assessments" className="hover:text-teal-400">SIL Assessments</Link></li>
              <li><Link to="/services/sda-assessments" className="hover:text-teal-400">SDA Assessments</Link></li>
              <li><Link to="/services/telehealth-fca" className="hover:text-teal-400">Telehealth FCA</Link></li>
              <li><Link to="/services/fca-for-autism" className="hover:text-teal-400">FCA for Autism</Link></li>
              <li><Link to="/services/psychosocial-fca" className="hover:text-teal-400">Psychosocial FCA</Link></li>
              <li><Link to="/services/neurological-fca" className="hover:text-teal-400">Neurological FCA</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Locations</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/locations/melbourne" className="hover:text-teal-400">Melbourne FCA</Link></li>
              <li><Link to="/locations/sydney" className="hover:text-teal-400">Sydney FCA</Link></li>
              <li><Link to="/locations/brisbane" className="hover:text-teal-400">Brisbane FCA</Link></li>
              <li><Link to="/locations/perth" className="hover:text-teal-400">Perth FCA</Link></li>
              <li><Link to="/locations/adelaide" className="hover:text-teal-400">Adelaide FCA</Link></li>
              <li><Link to="/locations/canberra" className="hover:text-teal-400">Canberra FCA</Link></li>
              <li><Link to="/locations/tasmania" className="hover:text-teal-400">Tasmania FCA</Link></li>
              <li><Link to="/locations/northern-territory" className="hover:text-teal-400">Northern Territory FCA</Link></li>
              <li><Link to="/locations" className="hover:text-teal-400 font-medium">All locations →</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Resources</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/resources" className="hover:text-teal-400">All Resources</Link></li>
              <li><Link to="/resources/ndis-functional-domains-explained" className="hover:text-teal-400">NDIS Functional Domains</Link></li>
              <li><Link to="/resources/ndis-reasonable-and-necessary" className="hover:text-teal-400">Reasonable & Necessary</Link></li>
              <li><Link to="/resources/ndis-plan-review-evidence" className="hover:text-teal-400">Plan Review Evidence</Link></li>
              <li><Link to="/resources/care-and-needs-scale-cans-guide" className="hover:text-teal-400">CANS Guide</Link></li>
              <li><Link to="/resources/ndis-fca-evidence-checklist" className="hover:text-teal-400">Free Checklist (PDF)</Link></li>
              <li><Link to="/pricing" className="hover:text-teal-400">Pricing & Funding</Link></li>
              <li><Link to="/faq" className="hover:text-teal-400">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Learn</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/learn" className="hover:text-teal-400 font-semibold">FCA Knowledge Hub</Link></li>
              <li><Link to="/learn/how-much-does-an-fca-cost" className="hover:text-teal-400">How much does an FCA cost?</Link></li>
              <li><Link to="/learn/can-a-physiotherapist-complete-an-fca" className="hover:text-teal-400">Can a physio complete an FCA?</Link></li>
              <li><Link to="/learn/do-i-need-an-fca-for-ndis" className="hover:text-teal-400">Do I need an FCA for NDIS?</Link></li>
              <li><Link to="/what-is-a-functional-capacity-assessment" className="hover:text-teal-400">What is an FCA?</Link></li>
              <li><Link to="/case-studies" className="hover:text-teal-400">Case Studies</Link></li>
              <li><Link to="/conditions" className="hover:text-teal-400">Conditions Library</Link></li>
              <li><Link to="/methodology" className="hover:text-teal-400">Our Methodology</Link></li>
              <li><Link to="/quality-assurance" className="hover:text-teal-400">Quality Assurance</Link></li>
              <li><Link to="/why-trust-us" className="hover:text-teal-400">Why Trust Us</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Phone size={16} className="text-teal-400 mt-0.5" /><a href={BIZ.phoneHref} className="hover:text-teal-400">{BIZ.phone}</a></li>
              <li className="flex items-start gap-2"><Mail size={16} className="text-teal-400 mt-0.5" /><a href={BIZ.emailHref} className="hover:text-teal-400 break-all">{BIZ.email}</a></li>
              <li className="flex items-start gap-2"><MapPin size={16} className="text-teal-400 mt-0.5" /><span>{BIZ.address}</span></li>
              <li className="flex items-start gap-2"><Clock size={16} className="text-teal-400 mt-0.5" /><span>{BIZ.hours}</span></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-8" data-testid="footer-additional-services">
          <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Additional Services</h3>
          <ul className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <li><Link to="/dsp-functional-capacity-assessment" className="hover:text-teal-400" data-testid="footer-dsp-link">Disability Support Pension (DSP) Assessments</Link></li>
          </ul>
        </div>

        <div className="border-t border-stone-800 mt-10 pt-8 pb-4 flex flex-col md:flex-row items-center justify-center md:justify-between gap-6" data-testid="footer-ndis-strip">
          <div className="flex items-center gap-4">
            <img
              src="/images/ndis-registered-provider-440.png"
              srcSet="/images/ndis-registered-provider-290.webp 290w, /images/ndis-registered-provider-440.webp 440w"
              sizes="102px"
              alt="I Heart NDIS — Registered NDIS Provider"
              className="h-12 w-auto shrink-0 bg-white/95 rounded-md p-1.5"
              loading="lazy"
              width="180"
              height="48"
              data-testid="footer-ndis-artwork"
            />
            <div className="text-[12.5px] text-stone-400 leading-snug max-w-xs">
              <div className="font-semibold text-stone-200">Registered NDIS Provider</div>
              <div>Registered with the NDIS Quality and Safeguards Commission.</div>
            </div>
          </div>
          <div className="text-[11.5px] text-stone-500 md:max-w-md md:text-right leading-relaxed">
            FCA Reports Australia is an independent Occupational-Therapist-led practice. The "I Heart NDIS" mark is the official Registered Provider artwork of the NDIS Quality and Safeguards Commission; FCA Reports Australia is not owned by or part of the NDIA or NDIS Commission.
          </div>
        </div>

        <div className="border-t border-stone-800 mt-6 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-stone-500">
          <div>
            <div className="text-stone-300 font-semibold">
              FCA Reports Australia Pty Ltd
              {BIZ.abn && !BIZ.abn.includes("00 000 000") ? <span className="text-stone-500 font-normal"> · {BIZ.abn}</span> : null}
            </div>
            <div className="mt-1">© {year} {BIZ.legal}. All rights reserved. Registered NDIS Provider.</div>
          </div>
          <div className="flex gap-5 flex-wrap">
            <Link to="/for-support-coordinators" className="hover:text-teal-400">Support Coordinators</Link>
            <Link to="/refer-a-participant" className="hover:text-teal-400">Refer a Participant</Link>
            <Link to="/work-with-us" className="hover:text-teal-400">Work With Us</Link>
            <Link to="/sitemap" className="hover:text-teal-400">Sitemap</Link>
            <Link to="/privacy-policy" className="hover:text-teal-400">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-teal-400">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
